import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, LayoutGrid, Loader2 } from "lucide-react";
import { TEMPLATES } from "@/lib/constants";

export default function PromptSection({ onGenerate, loading, error }) {
  const [prompt, setPrompt] = useState("");

  const handleTemplateClick = (template) => {
    setPrompt(template.prompt);
    onGenerate(template.prompt);
  };

  return (
    <section data-testid="prompt-section" className="max-w-[820px] mx-auto px-6 pb-6">
      <div className="rounded-sm p-5" style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)" }}>

        <div className="flex items-center gap-2 mb-3 text-xs font-medium" style={{ color: "var(--sdb-text)" }}>
          <Sparkles className="w-3 h-3" />Describe your database
        </div>

        <div data-testid="prompt-tab-content">
          <Textarea data-testid="prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={"Describe the database you need...\n\nExample: A Dubai real estate agency with properties, agents, listings, rental contracts, and maintenance requests. Include Dubai area names, AED pricing, and realistic property specs."} className="min-h-[120px] font-mono text-sm rounded-sm focus-visible:ring-0 resize-vertical" style={{ background: "var(--sdb-bg)", border: "1px solid var(--sdb-border)", color: "var(--sdb-text)", "--placeholder-color": "var(--sdb-text-subtle)" }} />
          <div className="flex justify-between items-center mt-3 flex-wrap gap-3">
            <span className="font-mono text-[10px] tracking-wide" style={{ color: "var(--sdb-text-subtle)" }}>Tip: industry + geography + function = better data</span>
            <Button data-testid="generate-btn" onClick={() => onGenerate(prompt)} disabled={loading || !prompt.trim()} className="gradient-bg text-black font-semibold text-xs rounded-sm px-5 py-2 h-auto glow-btn disabled:opacity-40 disabled:cursor-not-allowed hover:text-black">
              {loading ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Generating...</>) : "Generate Database"}
            </Button>
          </div>
        </div>

        <div className="pt-4 mt-4" style={{ borderTop: "1px solid var(--sdb-border)" }}>
          <div className="flex items-center gap-2 mb-3 text-xs font-medium" style={{ color: "var(--sdb-text)" }}>
            <LayoutGrid className="w-3 h-3" />Or start from a template
          </div>

          <div data-testid="templates-tab-content">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.id} data-testid={`template-${t.id}`} disabled={loading} onClick={() => handleTemplateClick(t)} className="template-card-hover text-left p-3.5 rounded-sm disabled:opacity-40 disabled:cursor-wait" style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)" }}>
                    <div className="flex justify-between items-start mb-2">
                      <Icon className="w-5 h-5" style={{ color: "var(--sdb-text-muted)" }} strokeWidth={1.5} />
                      <Badge variant="outline" className="rounded-none font-mono text-[8px] tracking-wider px-1.5 py-0 border-transparent" style={{ background: `${t.tagColor}15`, color: t.tagColor }}>{t.tag}</Badge>
                    </div>
                    <div className="text-[13px] font-medium mb-1" style={{ color: "var(--sdb-text)" }}>{t.title}</div>
                    <div className="text-[11px] leading-relaxed" style={{ color: "var(--sdb-text-muted)" }}>{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </div>
      {error && (
        <div data-testid="error-message" className="mt-4 p-3 rounded-sm font-mono text-xs leading-relaxed" style={{ background: "var(--sdb-accent-dim)", border: "1px solid var(--sdb-error)", color: "var(--sdb-error)" }}>{error}</div>
      )}
    </section>
  );
}
